import React from "react";
import {
  AiOutlineCalendar,
  AiOutlineCar,
  AiOutlineClockCircle,
  AiOutlineSolution,
  AiOutlineContainer,
  AiOutlineDollarCircle,
  AiOutlineFileText,
  AiOutlineFundProjectionScreen,
  AiOutlineMessage,
  AiOutlineTeam,
  AiOutlineEnvironment,
  AiOutlineSmile,
  AiOutlineSafetyCertificate,
  AiOutlineWhatsApp,
  AiOutlineSetting,
  AiOutlineAlert,
  AiOutlineCompass,
  AiOutlineInbox,
  AiOutlineCoffee,
  AiOutlineWallet,
} from "react-icons/ai";
import {
  HiOutlineAcademicCap,
  HiOutlineOfficeBuilding,
  HiOutlineUserGroup,
} from "react-icons/hi";
import { GoBook } from "react-icons/go";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import axios from "axios";
import { serverUrl } from "../../nestserver";
import { selectSchoolId } from "../../redux/slices/auth.slice";

const TONES = {
  Instructors: { card: "#dbeafe", circle: "#93c5fd", icon: "#1d4ed8" },
  "Support Staff": { card: "#ecfccb", circle: "#bef264", icon: "#3f6212" },
  Admissions: { card: "#ede9fe", circle: "#c4b5fd", icon: "#6d28d9" },
  Students: { card: "#d1fae5", circle: "#6ee7b7", icon: "#065f46" },
  Parent: { card: "#f3e8ff", circle: "#d8b4fe", icon: "#7e22ce" },
  Classes: { card: "#e0e7ff", circle: "#a5b4fc", icon: "#3730a3" },
  "Progress Card": { card: "#cffafe", circle: "#67e8f9", icon: "#0e7490" },
  Schedule: { card: "#ffe4e6", circle: "#fda4af", icon: "#be123c" },
  "Wake-up Alarm": { card: "#ffedd5", circle: "#fdba74", icon: "#c2410c" },
  Fee: { card: "#dcfce7", circle: "#86efac", icon: "#15803d" },
  Expenses: { card: "#fff1f2", circle: "#fecdd3", icon: "#9f1239" },
  Payroll: { card: "#eff6ff", circle: "#bfdbfe", icon: "#1e40af" },
  Message: { card: "#e0f2fe", circle: "#bae6fd", icon: "#0284c7" },
  "Class Announcements": {
    card: "#fce7f3",
    circle: "#f9a8d4",
    icon: "#be185d",
  },
  WhatsApp: { card: "#dcfce7", circle: "#86efac", icon: "#15803d" },
  "Student Attendance": { card: "#fef9c3", circle: "#fde047", icon: "#a16207" },
  "Day Care Activity": { card: "#ffe4d5", circle: "#fdba74", icon: "#c2410c" },
  "Staff Attendance": { card: "#f0fdfa", circle: "#99f6e4", icon: "#0f766e" },
  Transportation: { card: "#f5f5f4", circle: "#d6d3d1", icon: "#44403c" },
  "Live Bus Tracking": { card: "#fee2e2", circle: "#fca5a5", icon: "#b91c1c" },
  "Consent Forms": { card: "#ecfeff", circle: "#a5f3fc", icon: "#0e7490" },
  "Stock Inventory": { card: "#f7fee7", circle: "#d9f99d", icon: "#4d7c0f" },
  Homework: { card: "#e0f2fe", circle: "#7dd3fc", icon: "#0369a1" },

  Diary: { card: "#e0f2fe", circle: "#7dd3fc", icon: "#0369a1" },
  "Course Board": { card: "#fef2f2", circle: "#fecaca", icon: "#b91c1c" },
  Fun: { card: "#fdf4ff", circle: "#f5d0fe", icon: "#86198f" },
  "Certificate Builder": {
    card: "#fef3c7",
    circle: "#fcd34d",
    icon: "#92400e",
  },
};

const CATEGORIES = [
  {
    label: "People",
    items: ["Instructors", "Support Staff", "Admissions", "Students", "Parent"],
  },
  {
    label: "Academic",
    items: ["Classes", "Course Board", "Progress Card", "Schedule", "Homework", "Diary"],
  },
  { label: "Finance", items: ["Fee", "Expenses", "Payroll"] },
  {
    label: "Communication",
    items: ["Message", "Class Announcements", "WhatsApp", "Wake-up Alarm"],
  },
  { label: "Attendance", items: ["Student Attendance", "Day Care Activity", "Staff Attendance"] },
  {
    label: "Operations",
    items: ["Transportation", "Live Bus Tracking", "Consent Forms", "Stock Inventory"],
  },
  { label: "Learning", items: ["Fun"] },
  { label: "Documents", items: ["Certificate Builder"] },
  { label: "Settings", items: ["School Setting"] },
];

const AdminMontessoriMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const schoolId = useSelector(selectSchoolId);

  const school = useQuery(
    ["school", schoolId],
    () =>
      axios
        .get(`${serverUrl}/school/${schoolId}`, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((r) => r.data),
    { enabled: !!schoolId },
  );

  const isTransportationEnabled =
    school?.data?.result?.data?.is_transportation_enabled !== false;
  const isLiveTrackingEnabled =
    school?.data?.result?.data?.is_live_tracking_enabled !== false;

  const allItems = [
    {
      title: "Instructors",
      icon: <AiOutlineTeam size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/instructor"),
      url: "/dashboard/admin/menu/instructor",
    },
    {
      title: "Admissions",
      icon: <HiOutlineOfficeBuilding size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/admission"),
      url: "/dashboard/admin/menu/admission",
    },
    {
      title: "Students",
      icon: <HiOutlineAcademicCap size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/student"),
      url: "/dashboard/admin/menu/student",
    },
    {
      title: "Parent",
      icon: <HiOutlineUserGroup size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/parent"),
      url: "/dashboard/admin/menu/parent",
    },
        {
      title: "Support Staff",
      icon: <AiOutlineTeam size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/support-staff"),
      url: "/dashboard/admin/menu/support-staff",
    },
    {
      title: "Classes",
      icon: <AiOutlineFundProjectionScreen size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/class"),
      url: "/dashboard/admin/menu/class",
    },
    {
      title: "Course Board",
      icon: <GoBook size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/course-board"),
      url: "/dashboard/admin/menu/course-board",
    },
    {
      title: "Progress Card",
      icon: <AiOutlineFileText size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/montessori-report"),
      url: "/dashboard/admin/menu/montessori-report",
    },
    {
      title: "Schedule",
      icon: <AiOutlineClockCircle size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/schedule"),
      url: "/dashboard/admin/menu/schedule",
    },
    {
      title: "Wake-up Alarm",
      icon: <AiOutlineAlert size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/alarm"),
      url: "/dashboard/admin/menu/alarm",
    },
    {
      title: "Fee",
      icon: <AiOutlineDollarCircle size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/fee/dashboard"),
      url: "/dashboard/admin/menu/fee",
    },
    {
      title: "Expenses",
      icon: <AiOutlineDollarCircle size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/expenses"),
      url: "/dashboard/admin/menu/expenses",
    },
    {
      title: "Payroll",
      icon: <AiOutlineWallet size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/payroll"),
      url: "/dashboard/admin/menu/payroll",
    },
    {
      title: "Message",
      icon: <AiOutlineMessage size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/message"),
      url: "/dashboard/admin/menu/message",
    },
    {
      title: "Class Announcements",
      icon: <AiOutlineFileText size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/class-announcements"),
      url: "/dashboard/admin/menu/class-announcements",
    },
    {
      title: "WhatsApp",
      icon: <AiOutlineWhatsApp size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/whatsapp/groups"),
      url: "/dashboard/admin/menu/whatsapp",
    },
    {
      title: "Student Attendance",
      icon: <AiOutlineCalendar size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/attendance"),
      url: "/dashboard/admin/menu/attendance",
    },
    {
      title: "Day Care Activity",
      icon: <AiOutlineCoffee size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/daily-activity"),
      url: "/dashboard/admin/menu/daily-activity",
    },
    {
      title: "Staff Attendance",
      icon: <AiOutlineEnvironment size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/instructor-attendance"),
      url: "/dashboard/admin/menu/instructor-attendance",
    },
    {
      title: "Transportation",
      icon: <AiOutlineCar size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/transportation"),
      url: "/dashboard/admin/menu/transportation",
    },
    {
      title: "Live Bus Tracking",
      icon: <AiOutlineEnvironment size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/live-tracking"),
      url: "/dashboard/admin/menu/live-tracking",
    },
    {
      title: "Consent Forms",
      icon: <AiOutlineCompass size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/consent-form/forms"),
      url: "/dashboard/admin/menu/consent-form",
    },
    {
      title: "Stock Inventory",
      icon: <AiOutlineInbox size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/stock"),
      url: "/dashboard/admin/menu/stock",
    },
    {
      title: "Homework",
      icon: <AiOutlineContainer size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/homework"),
      url: "/dashboard/admin/menu/homework",
    },
    {
      title: "Diary",
      icon: <AiOutlineSolution size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/diary"),
      url: "/dashboard/admin/menu/diary",
    },
    {
      title: "Fun",
      icon: <AiOutlineSmile size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/fun"),
      url: "/dashboard/admin/menu/fun",
    },
    {
      title: "Certificate Builder",
      icon: <AiOutlineSafetyCertificate size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/certificate-builder"),
      url: "/dashboard/admin/menu/certificate-builder",
    },
    {
      title: "School Setting",
      icon: <AiOutlineSetting size={28} />,
      onClick: () => navigate("/dashboard/admin/menu/school-settings"),
      url: "/dashboard/admin/menu/school-settings",
    },
  ].filter((item) => {
    if (item.title === "Transportation" && !isTransportationEnabled)
      return false;
    if (item.title === "Live Bus Tracking" && !isLiveTrackingEnabled)
      return false;
    return true;
  });

  return (
    <div className="p-8">
      <div className="bg-white p-8 rounded-2xl">
        {CATEGORIES.map(({ label, items: titles }) => {
          const catItems = allItems.filter((i) => titles.includes(i.title));
          if (!catItems.length) return null;
          return (
            <div key={label} style={{ marginBottom: 32 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 14,
                  paddingBottom: 8,
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                {label}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 16,
                }}
              >
                {catItems.map((item) => {
                  const tone = TONES[item.title] || {
                    card: "#f1f5f9",
                    circle: "#cbd5e1",
                    icon: "#475569",
                  };
                  const isActive = location.pathname.startsWith(item.url);
                  return (
                    <div
                      key={item.title}
                      onClick={item.onClick}
                      style={{
                        background: tone.card,
                        borderRadius: 20,
                        padding: "20px 12px 16px",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 12,
                        boxShadow: isActive
                          ? `0 0 0 2.5px ${tone.icon}`
                          : "0 2px 8px rgba(0,0,0,0.06)",
                        transition: "all 0.15s ease",
                        minHeight: 120,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow =
                          "0 8px 20px rgba(0,0,0,0.11)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = isActive
                          ? `0 0 0 2.5px ${tone.icon}`
                          : "0 2px 8px rgba(0,0,0,0.06)";
                      }}
                    >
                      <div
                        style={{
                          width: 58,
                          height: 58,
                          borderRadius: "50%",
                          background: tone.circle,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: tone.icon,
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          textAlign: "center",
                          lineHeight: 1.35,
                          color: "#1e3a5f",
                        }}
                      >
                        {item.title}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminMontessoriMenu;
